import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Difficulty, Streak, StyledFlex } from '@tryhackme/thm-ui-components';
import { useAppDispatch } from 'src/app/hooks';
import { RTKQueryTag } from 'src/common/constants';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { useMediaQuery } from 'src/common/hooks/use-media-query';
import { useRepairStreak } from 'src/common/hooks/use-repair-streak';
import { LocationState } from 'src/common/interfaces/analytics';
import { extendedApiSlice } from 'src/common/slices';
import { useDelayedPathEnrollmentExperiment } from 'src/features/welcome/hooks/use-delayed-path-enrollment-experiment';
import { useRoomCode } from 'src/features/room/hooks/use-room-replacement-experiment';
import { RoomBannerActions } from '../../room-banner/components';
import { StyledLoading, StyledRoomActionsWrapper, StyledRoomDescriptionWrapper, StyledRoomHeaderWrapper, StyledRoomImage, StyledRoomTitle, StyledSideSectionCloseBtn, StyledRightSectionWrapper } from './room-header.styles';
import { RoomHeaderProps } from './room-header.types';
import { useRoomTaskBreakdownContext } from './room-task-breakdown-context';
export const RoomHeader = ({
  streak,
  roomDetails,
  isPrivateRoom,
  isRetiredRoom,
  setRoomProgress,
  setShowVideo,
  splitScreenTabs,
  vmStartType,
  isSplitScreenOpen
}: RoomHeaderProps) => {
  const {
    currentTask
  } = useRoomTaskBreakdownContext();
  const roomCode = useRoomCode();
  if (!roomCode) throw new Error('No roomCode in url params');
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.TABLET}px)`);
  const isTabletAndNotSplitScreen = isTablet && !isSplitScreenOpen;
  const {
    state: historyState
  } = (useLocation() as {
    state: LocationState;
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    isDelayedPathEnrollmentEnabled
  } = useDelayedPathEnrollmentExperiment();
  const handleCloseHeader = () => {
    dispatch(extendedApiSlice.util.invalidateTags([RTKQueryTag.PathsCurrent, RTKQueryTag.PathsCurrentModule]));
    if (isDelayedPathEnrollmentEnabled) {
      navigate('/hacktivities');
      return;
    }
    navigate(historyState?.pageReferrer ?? '/dashboard');
  };
  const repairStreakHandler = useRepairStreak();
  const isLoading = !roomDetails;
  const roomHeaderRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (currentTask?.taskNo) {
      roomHeaderRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [currentTask?.taskNo]);
  return <StyledRoomHeaderWrapper $isTabletAndNotSplitScreen={isTabletAndNotSplitScreen} ref={roomHeaderRef} data-testid="room-header" data-sentry-element="StyledRoomHeaderWrapper" data-sentry-component="RoomHeader" data-sentry-source-file="room-header.tsx">
      <StyledFlex style={{
      flex: 1
    }} data-sentry-element="StyledFlex" data-sentry-source-file="room-header.tsx">
        {isLoading ? <StyledLoading width="35.6rem" data-testid="loading" /> : <StyledRoomDescriptionWrapper>
            <StyledRoomImage src={roomDetails.imageURL} errorSrc="https://assets.tryhackme.com/img/banners/default_tryhackme.png" aria-label="Room Icon" />
            <StyledFlex gap="1.6rem">
              <StyledRoomTitle>{roomDetails.title}</StyledRoomTitle>
              <Difficulty difficulty={roomDetails.difficulty} hasIcon isDarkVersion />
            </StyledFlex>
          </StyledRoomDescriptionWrapper>}
        {isLoading ? <StyledLoading width="50.6rem" /> : <StyledRoomActionsWrapper>
            <RoomBannerActions roomDetails={roomDetails} isPrivateOrRetiredRoom={isPrivateRoom || isRetiredRoom} isPrivateRoom={isPrivateRoom} isRetiredRoom={isRetiredRoom} setRoomProgress={setRoomProgress} setShowVideo={setShowVideo} splitScreenTabs={splitScreenTabs} vmStartType={vmStartType} />
          </StyledRoomActionsWrapper>}
      </StyledFlex>
      <StyledRightSectionWrapper data-sentry-element="StyledRightSectionWrapper" data-sentry-source-file="room-header.tsx">
        {streak && <Streak {...streak} onStreakRepair={repairStreakHandler} />}
        <StyledSideSectionCloseBtn onClick={handleCloseHeader} aria-label="close" data-sentry-element="StyledSideSectionCloseBtn" data-sentry-source-file="room-header.tsx">
          <FontAwesomeIcon icon={faXmark} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="room-header.tsx" />
        </StyledSideSectionCloseBtn>
      </StyledRightSectionWrapper>
    </StyledRoomHeaderWrapper>;
};